// Twilio Video Configuration
export const TWILIO_CONFIG = {
  // Media constraints for Twilio createLocalTracks
  MEDIA_CONSTRAINTS: {
    video: {
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 30 }
    },
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true
    }
  },
  
  // Room connection options
  CONNECT_OPTIONS: {
    dominantSpeaker: true,
    networkQuality: {
      local: 1,
      remote: 1
    }
  }
}

// User Roles - Video Call Participants
export const USER_ROLES = {
  INTERVIEWER: 'interviewer',
  INTERVIEWEE: 'interviewee'
}

// Company User Roles
export const COMPANY_USER_ROLES = {
  INTERVIEWER: 'interviewer',
  RECRUITER: 'recruiter',
  ADMIN: 'admin',
  HIRING_MANAGER: 'hiring_manager'
}

// User Types
export const USER_TYPES = {
  COMPANY: 'company',
  CANDIDATE: 'candidate'
}

// Interview Types
export const INTERVIEW_TYPES = {
  SCREENING: 'screening',
  TECHNICAL: 'technical',
  BEHAVIORAL: 'behavioral',
  FINAL: 'final',
  OTHER: 'other'
}

// Interview Type Options (for dropdowns)
export const INTERVIEW_TYPE_OPTIONS = [
  { value: 'screening', label: 'Screening Interview' },
  { value: 'technical', label: 'Technical Interview' },
  { value: 'behavioral', label: 'Behavioral Interview' },
  { value: 'final', label: 'Final Interview' },
  { value: 'other', label: 'Other' }
]

// Room Status
export const ROOM_STATUS = {
  SCHEDULED: 'scheduled',
  ACTIVE: 'active',
  RECORDING: 'recording',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  ARCHIVED: 'archived'
}

// Room Status Labels
export const ROOM_STATUS_LABELS = {
  scheduled: 'Scheduled',
  active: 'Active',
  recording: 'Recording',
  completed: 'Completed',
  cancelled: 'Cancelled',
  archived: 'Archived'
}

// Recording Status
export const RECORDING_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  READY: 'ready',
  FAILED: 'failed',
  DELETED: 'deleted'
}

// Recommendation levels
export const RECOMMENDATION_LEVELS = {
  STRONG_YES: 'strong_yes',
  YES: 'yes',
  MAYBE: 'maybe',
  NO: 'no',
  STRONG_NO: 'strong_no'
}

// Assessment Decision
export const ASSESSMENT_DECISION = {
  HIRE: 'hire',
  REJECT: 'reject',
  NEXT_ROUND: 'next_round',
  HOLD: 'hold'
}

// Assessment Decision Labels
export const ASSESSMENT_DECISION_LABELS = {
  hire: 'Hire',
  reject: 'Reject',
  next_round: 'Move to Next Round',
  hold: 'Put on Hold'
}

// Experience Levels
export const EXPERIENCE_LEVELS = {
  JUNIOR: 'junior',
  MID: 'mid',
  SENIOR: 'senior',
  LEAD: 'lead',
  PRINCIPAL: 'principal'
}

// Experience Level Options
export const EXPERIENCE_LEVEL_OPTIONS = [
  { value: 'junior', label: 'Junior (0-2 years)' },
  { value: 'mid', label: 'Mid-level (2-5 years)' },
  { value: 'senior', label: 'Senior (5-10 years)' },
  { value: 'lead', label: 'Lead (10+ years)' },
  { value: 'principal', label: 'Principal/Staff (15+ years)' }
]

// Storage keys
export const STORAGE_KEYS = {
  ROOM_CREATOR: 'room_creator_',
  USER_ROLE: 'user_role_',
  VIDEO_ROOM: 'videoRoom_',
  INTERVIEW_HISTORY: 'interviewHistory',
  AUTH_TOKEN: 'token'
}

// Speech Recognition
export const SPEECH_CONFIG = {
  LANG: 'en-US',
  CONTINUOUS: true,
  INTERIM_RESULTS: true,
  MAX_ALTERNATIVES: 1
}

// Timers
export const TIMER_INTERVAL = 1000 // 1 second
export const RECONNECT_DELAY = 2000 // 2 seconds
export const AUTO_TRANSCRIPT_DELAY = 2000 // 2 seconds after connection
export const STATUS_POLL_INTERVAL = 5000 // 5 seconds

// File size limits
export const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB

// API Endpoints
export const API_ENDPOINTS = {
  // Twilio Video
  TWILIO_START: '/video/call/twilio/start',
  TWILIO_STOP: '/video/call/twilio/stop',
  TWILIO_STATUS: '/video/call/twilio/status',
  TWILIO_DOWNLOAD: '/video/call/twilio/download-recordings',
  TWILIO_TEST: '/video/call/twilio/test',

  // Room Management
  ROOM_DETAILS: '/video/room',
  ROOM_LIST: '/video/room/list',
  ROOM_CANDIDATE_LIST: '/video/room/candidate/list',
  ROOM_ARCHIVE: '/video/room/:roomId/archive',
  ROOM_DELETE: '/video/room/:roomId',
  ROOM_STATS: '/video/room/stats',

  // Transcripts
  TRANSCRIPT_GET: '/video/room/:roomId/transcript',
  TRANSCRIPT_VISIBILITY: '/video/room/:roomId/transcript/visibility',

  // Assessments
  ASSESSMENT_GET: '/video/room/:roomId/assessment',
  ASSESSMENT_REQUIREMENTS: '/video/room/:roomId/assessment/requirements',
  ASSESSMENT_VISIBILITY: '/video/room/:roomId/assessment/visibility',
  ASSESSMENT_REVIEW: '/video/room/:roomId/assessment/review',

  // Recordings
  RECORDINGS_GET: '/video/room/:roomId/recordings',

  // Access Control
  ACCESS_GRANT: '/video/room/:roomId/access/grant',
  ACCESS_REVOKE: '/video/room/:roomId/access/revoke',
  SHARE: '/video/room/:roomId/share',
  UNSHARE: '/video/room/:roomId/unshare'
}

// Error Messages
export const ERROR_MESSAGES = {
  // Validation errors
  USER_REQUIRED: 'User information is required',
  CANDIDATE_REQUIRED: 'Candidate information is required',
  ROOM_ID_REQUIRED: 'Room ID is required',
  INVALID_ROOM_ID: 'Invalid or expired room ID',

  // Permission errors
  UNAUTHORIZED: 'You are not authorized to access this resource',
  COMPANY_USER_ONLY: 'This action requires company user access',
  ROOM_OWNER_ONLY: 'This action can only be performed by the room owner',
  ACCESS_DENIED: 'Access denied to this interview',

  // Twilio errors
  TWILIO_NOT_CONFIGURED: 'Twilio service is not configured',
  TWILIO_CONNECTION_FAILED: 'Failed to connect to Twilio service',
  RECORDING_START_FAILED: 'Failed to start recording',
  RECORDING_STOP_FAILED: 'Failed to stop recording',

  // Room errors
  ROOM_NOT_FOUND: 'Interview room not found',
  ROOM_ALREADY_COMPLETED: 'This interview has already been completed',
  ROOM_NOT_ACTIVE: 'This interview is not currently active',

  // Network errors
  NETWORK_ERROR: 'Network connection error. Please check your internet connection.',
  API_ERROR: 'An error occurred while communicating with the server',
  TIMEOUT_ERROR: 'Request timed out. Please try again.',

  // Media errors
  CAMERA_NOT_FOUND: 'Camera not found. Please check your device settings.',
  MICROPHONE_NOT_FOUND: 'Microphone not found. Please check your device settings.',
  MEDIA_PERMISSION_DENIED: 'Permission denied to access camera or microphone',

  // Generic errors
  UNKNOWN_ERROR: 'An unknown error occurred',
  OPERATION_FAILED: 'Operation failed. Please try again.'
}

// Success Messages
export const SUCCESS_MESSAGES = {
  INTERVIEW_STARTED: 'Interview started successfully',
  INTERVIEW_JOINED: 'Joined interview successfully',
  INTERVIEW_ENDED: 'Interview ended successfully',
  ACCESS_GRANTED: 'Access granted successfully',
  ACCESS_REVOKED: 'Access revoked successfully',
  ASSESSMENT_UPDATED: 'Assessment updated successfully',
  TRANSCRIPT_UPDATED: 'Transcript updated successfully',
  INTERVIEW_SHARED: 'Interview shared successfully',
  INTERVIEW_ARCHIVED: 'Interview archived successfully'
}

// Default Values
export const DEFAULTS = {
  INTERVIEW_TYPE: 'technical',
  POLL_INTERVAL: 5000,
  MAX_RETRIES: 3,
  RETRY_DELAY: 5000,
  TOKEN_TTL: 14400, // 4 hours in seconds
  PAGE_SIZE: 20
}
